/** @format */
import React, { FC, useState, useEffect } from 'react'
import {
  _$,
  Message,
  Dialog,
  Button,
  Table,
  Tabs,
  Tab,
  Icon,
  Pagination,
  TableColumn
} from '@king-design/react'
import { useAsyncFn } from 'react-use'
import {
  GetPolicyList,
  GetEventPolicy,
  DeleteAlarmPolicy,
  DeleteEventPolicy
} from 'src/api/ApiAlarmServer'
import { omit } from 'lodash'

interface ReferencedDialogProps {
  visible: boolean
  templateData: any
  onClose: (update?: boolean) => void
}

const initPage = { PageIndex: 1, PageSize: 20 }
const ReferencedDialog: FC<ReferencedDialogProps> = ({ visible, templateData, onClose }) => {
  const [tabval, setTabval] = useState<'threshold' | 'event'>('threshold')
  const [thresholdSelected, setThresholdSelected] = useState<number[]>([])
  const [thresholdPage, setThresholdPage] = useState(initPage)
  const [eventSelected, setEventSelected] = useState<string[]>([])
  const [eventPage, setEventPage] = useState(initPage)

  const threshold_scheme = {
    policyName: {
      title: _$('策略名称')
    },
    instanceIds: {
      title: _$('关联实例'),
      width: 200,
      template: function (item) {
        return (
          <>
            {item?.resourceBindType === 2 ? (
              <div>
                <p style={{ margin: 0 }}>
                  {_$('项目')}：{item.projectName || ''}
                </p>
                <p style={{ margin: 0 }}>
                  {_$('实例')}：
                  {item.instanceIds ? item.instanceIds.length : item.instanceInfoList.length}
                </p>
              </div>
            ) : null}
            {item?.resourceBindType === 127 ? (
              <p>
                {_$('项目')}：{item.projectName || ''}
                <span className="deleted_color">({_$('已删除')})</span>
              </p>
            ) : null}
            {item?.resourceBindType !== 2 && item?.resourceBindType !== 127 ? (
              <span>
                {item.instanceIds ? item.instanceIds.length : item.instanceInfoList.length}
              </span>
            ) : null}
          </>
        )
      }
    }
  }
  const event_scheme = {
    policyName: {
      title: _$('策略名称')
    },
    resouceBindType: {
      title: _$('关联实例'),
      width: 200,
      template: function (item) {
        return (
          <span>
            {item.resourceBindType &&
            (item.resourceBindType === 2 || item.resourceBindType === 127) ? (
              <p style={{ margin: 0 }}>
                {_$('项目')}：{item.projectName || ''}
                {item.resourceBindType === 127 ? (
                  <span className="deleted_color">{_$('（已删除）')}</span>
                ) : null}
              </p>
            ) : null}
            <p style={{ margin: 0 }}>
              {_$('实例')}：{item.instanceCount || 0}
            </p>
          </span>
        )
      }
    }
  }

  useEffect(() => {
    if (visible && templateData) {
      if (templateData?.ThresholdReferencedPolicies > 0) {
        setTabval('threshold')
      } else if (templateData?.EventReferencedPolicies > 0) {
        setTabval('event')
      }
    }
  }, [visible, templateData])

  useEffect(() => {
    if (visible && templateData) {
      threshold_fetch()
    }
  }, [visible, templateData, thresholdPage])

  useEffect(() => {
    if (visible && templateData) {
      event_fetch()
    }
  }, [visible, templateData, eventPage])

  const [{ value: threshold_list }, threshold_fetch] = useAsyncFn(async () => {
    if (templateData?.TemplateID) {
      const { code, data } = await GetPolicyList({
        TemplateId: templateData?.TemplateID,
        ProductType: templateData?.ProductType,
        ...thresholdPage
      })
      if (code === 'Success') {
        return {
          data: data?.alarmPolicyList || [],
          total: data?.totalCount || 0
        }
      }
      return {}
    }
  }, [templateData, thresholdPage])

  const [{ value: event_list }, event_fetch] = useAsyncFn(async () => {
    if (templateData?.TemplateID) {
      const { code, data } = await GetEventPolicy({
        TemplateId: templateData?.TemplateID,
        ProductType: templateData?.ProductType,
        ...eventPage
      })
      if (code === 'Success') {
        return {
          data: data?.policyList || [],
          total: data?.totalCount || 0
        }
      }
      return {}
    }
  }, [templateData, eventPage])

  // 删除阈值告警策略
  const deleteThreshold = async () => {
    if (thresholdSelected?.length) {
      const { code } = await DeleteAlarmPolicy({ PolicyIds: thresholdSelected })
      if (code === 'Success') {
        Message.success(_$('删除成功'))
        threshold_fetch()
        setThresholdPage(initPage)
      }
    }
  }

  // 删除事件告警策略
  const deleteEvent = async () => {
    if (eventSelected?.length) {
      const { code } = await DeleteEventPolicy(eventSelected)
      if (code === 'Success') {
        Message.success(_$('删除成功'))
        event_fetch()
        setEventPage(initPage)
      }
    }
  }

  const onDialogClose = () => {
    onClose(true)
    setThresholdSelected([])
    setThresholdPage(initPage)
    setEventSelected([])
    setEventPage(initPage)
  }

  return (
    <Dialog
      width={800}
      value={visible}
      mode="destroy"
      title={templateData?.TemplateName || ''}
      onChangeValue={show => {
        if (!show && visible !== show) {
          onDialogClose()
        }
      }}
      slotFooter={
        <>
          <Button
            type="primary"
            onClick={() => {
              onDialogClose()
            }}
          >
            {_$('关闭')}
          </Button>
        </>
      }
    >
      <Tabs value={tabval} onChangeValue={tab => setTabval(tab)}>
        {templateData?.ThresholdReferencedPolicies ? (
          <Tab value="threshold">{_$('阈值告警')}</Tab>
        ) : null}
        {templateData?.EventReferencedPolicies ? <Tab value="event">{_$('事件告警')}</Tab> : null}
      </Tabs>
      {tabval === 'threshold' && templateData?.ThresholdReferencedPolicies ? (
        <div className="marginTop">
          <Button
            disabled={!thresholdSelected?.length}
            onClick={() => {
              deleteThreshold()
            }}
          >
            <Icon className="ion-trash-a" />
            {_$('批量删除')}
          </Button>
          <Table
            className="marginTop"
            data={threshold_list?.data || []}
            rowKey={({ policyId }) => policyId}
            onChangeCheckedKeys={item => {
              setThresholdSelected(item)
            }}
            checkedKeys={thresholdSelected}
          >
            {threshold_scheme
              ? Object.keys(threshold_scheme).map(key => {
                  if (typeof threshold_scheme[key] === 'string') {
                    return <TableColumn title={threshold_scheme[key]} key={key} />
                  }
                  const { title, template } = threshold_scheme[key]
                  const filterProps = omit(threshold_scheme[key], 'template', 'title')
                  if (template) {
                    filterProps.slotTemplate = ([data, index]) => template(data, index)
                  }
                  return <TableColumn key={key} slotTitle={title} {...filterProps} />
                })
              : null}
          </Table>
          <Pagination
            size="small"
            total={threshold_list?.total}
            value={thresholdPage?.PageIndex}
            limit={thresholdPage?.PageSize}
            onChange={({ value, limit }) => {
              setThresholdPage({
                PageIndex: value,
                PageSize: limit
              })
            }}
          />
        </div>
      ) : null}
      {tabval === 'event' && templateData?.EventReferencedPolicies ? (
        <div className="marginTop">
          <Button
            disabled={!eventSelected?.length}
            onClick={() => {
              deleteEvent()
            }}
          >
            <Icon className="ion-trash-a" />
            {_$('批量删除')}
          </Button>
          <Table
            className="marginTop"
            data={event_list?.data || []}
            rowKey={({ policyName }) => policyName}
            onChangeCheckedKeys={item => {
              setEventSelected(item)
            }}
            checkedKeys={eventSelected}
          >
            {event_scheme
              ? Object.keys(event_scheme).map(key => {
                  if (typeof event_scheme[key] === 'string') {
                    return <TableColumn title={event_scheme[key]} key={key} />
                  }
                  const { title, template } = event_scheme[key]
                  const filterProps = omit(event_scheme[key], 'template', 'title')
                  if (template) {
                    filterProps.slotTemplate = ([data, index]) => template(data, index)
                  }
                  return <TableColumn key={key} slotTitle={title} {...filterProps} />
                })
              : null}
          </Table>
          <Pagination
            size="small"
            total={event_list?.total}
            value={eventPage?.PageIndex}
            limit={eventPage?.PageSize}
            onChange={({ value, limit }) => {
              setEventPage({
                PageIndex: value,
                PageSize: limit
              })
            }}
          />
        </div>
      ) : null}
    </Dialog>
  )
}

export default ReferencedDialog
